/**
 * Sentry client-side init (BS-2146).
 *
 * NCUA examiner readiness: scrub PII before transmission per credit union
 * data handling policy. The `beforeSend` hook below routes every captured
 * event through `scrubEvent` (see `src/lib/sentry/pii-scrubber.ts`) which
 * walks the payload and redacts fields whose normalized name matches the
 * PII deny-list (SSN, DOB, account/routing numbers, names, contact info,
 * card numbers, etc.) BEFORE the event is transmitted to Sentry.
 *
 * Init is gated on `NEXT_PUBLIC_SENTRY_DSN` being set. With no DSN, the SDK
 * is effectively a no-op - useful for local dev / preview builds before the
 * DSN is provisioned.
 *
 * BS-2177: Integration list is explicit. `@sentry/nextjs` normally auto-adds
 * `browserTracingIntegration` (a large tracing chunk) even when
 * `tracesSampleRate: 0`. The `bundleSizeOptimizations.excludeTracing: true`
 * option in next.config.mjs handles the build-time tree-shake; the explicit
 * `integrations` filter here is belt-and-suspenders for environments where
 * the Turbopack define-replacement may not have fired (e.g. local dev
 * without a production build).
 *
 * BS-2185: The integrations filter ALSO replaces the default `Breadcrumbs`
 * integration with a `{ dom: false }` variant. The default fires on every
 * keystroke (it wires a global `keypress`/`input` listener and walks the
 * DOM to build a breadcrumb object, then runs `beforeBreadcrumb`'s PII
 * scrubber), which was the root cause of perceptible keystroke lag in
 * OLA form fields. Disabling `dom` keeps console / fetch / xhr / history
 * breadcrumbs (valuable for error context) while removing the per-input
 * synchronous work.
 *
 * The integration list construction lives in `src/lib/sentry/integrations.ts`
 * so it can be unit-tested independently (the test asserts both
 * suppressions are still in place; future "innocent" refactors that drop
 * either suppression will fail CI rather than silently regress).
 */
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:person_index/employment\",\"regex\":\"^/([^/]+)/employment$\",\"paramNames\":[\"person_index\"],\"hasOptionalPrefix\":false},{\"path\":\"/:person_index/personal-info\",\"regex\":\"^/([^/]+)/personal-info$\",\"paramNames\":[\"person_index\"],\"hasOptionalPrefix\":false},{\"path\":\"/:person_index/residence-info\",\"regex\":\"^/([^/]+)/residence-info$\",\"paramNames\":[\"person_index\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/co-borrower/selection\"},{\"path\":\"/confirmation\"},{\"path\":\"/gsap\"},{\"path\":\"/loan-details\"},{\"path\":\"/loan-selection\"},{\"path\":\"/login\"},{\"path\":\"/references\"},{\"path\":\"/summary\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.4";import * as Sentry from '@sentry/nextjs';

import { buildIntegrations } from '~/lib/sentry/integrations';
import { scrubBreadcrumb, scrubEvent, type ScrubbableEvent } from '~/lib/sentry/pii-scrubber';

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,

    // Per BS-2140: explicitly opt OUT of Sentry's default PII collection.
    // We do our own scrubbing in beforeSend; this is defense in depth.
    sendDefaultPii: false,

    // Tag every event with the deployment environment so dashboards can
    // filter preview vs staging vs prod. Defaults to "development" on local
    // dev sessions (no Vercel env var set).
    environment:
      process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ??
      process.env.NEXT_PUBLIC_VERCEL_ENV ??
      process.env.NODE_ENV ??
      'development',

    // Release tag - pulled from Vercel's git SHA env when available so
    // source maps + dashboards can join on it.
    release:
      process.env.NEXT_PUBLIC_SENTRY_RELEASE ??
      process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ??
      undefined,

    // Error reporting only for v1. Performance / tracing is out of scope
    // for BS-2146 (separate follow-up ticket).
    tracesSampleRate: 0,

    // BS-2177 + BS-2185: filter the default integration list.
    //   - BS-2177 drops `BrowserTracing` (large tracing chunk, runtime
    //     counterpart to `bundleSizeOptimizations.excludeTracing: true`
    //     in next.config.mjs).
    //   - BS-2185 replaces the default `Breadcrumbs` integration with a
    //     `{ dom: false }` variant so the SDK no longer wires a global
    //     `keypress`/`input` listener that captured a breadcrumb (with
    //     DOM-walk + PII scrubber pass) for every keystroke in OLA form
    //     fields. Console / fetch / xhr / history breadcrumb sources
    //     stay active.
    // See `src/lib/sentry/integrations.ts` for the helper and the unit
    // test that asserts both suppressions remain in place.
    integrations: buildIntegrations,

    // Suppress noisy / known-benign client errors. Adjust as we learn.
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Generic ResizeObserver loop limit warnings (Chrome non-error)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],

    /**
     * Compliance gate. Every event passes through scrubEvent before
     * transmission. If scrubbing throws, we drop the event rather than
     * risk shipping raw member data.
     */
    beforeSend(event) {
      try {
        return scrubEvent(event as unknown as ScrubbableEvent) as unknown as typeof event;
      } catch {
        // Fail closed: dropping an event is safer than transmitting PII.
        return null;
      }
    },

    /**
     * Same compliance contract for breadcrumbs (navigation, console, fetch
     * activity, etc.). beforeBreadcrumb fires before each breadcrumb is
     * attached to an event. BS-2167: scrubs both `data` (recursive) and
     * `message` (value-pattern) - navigation breadcrumbs put URLs in
     * `message`, and a `?email=...` query string would otherwise leak.
     */
    beforeBreadcrumb(breadcrumb) {
      try {
        return scrubBreadcrumb(breadcrumb);
      } catch {
        return null;
      }
    },
  });
}

/**
 * Required by `next/navigation` integration so Sentry can capture router
 * transitions as breadcrumbs.
 */
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
